import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export const LLM_LAB_INPUT_TOKEN_BUCKETS = ["0-500", "500-1000", "1000-2000", "2000-4000", "4000-8000", "8000+"] as const;
export const LLM_LAB_OUTPUT_TOKEN_BUCKETS = ["0-100", "100-300", "300-1000", "1000-2000", "2000-4000", "4000+"] as const;
export type LlmLabInputTokenBucket = (typeof LLM_LAB_INPUT_TOKEN_BUCKETS)[number];
export type LlmLabOutputTokenBucket = (typeof LLM_LAB_OUTPUT_TOKEN_BUCKETS)[number];

export type LlmLabScope = "global" | "organization" | "agent";

export type LlmLabAggregate = {
  provider: string;
  model: string;
  responseTimeP50: number | null;
  responseTimeP95: number | null;
  ttftP50: number | null;
  ttftP95: number | null;
  inputTokensP50: number | null;
  inputTokensP95: number | null;
  outputTokensP50: number | null;
  outputTokensP95: number | null;
  sampleCount: number;
  streamingSampleCount: number;
};

// One time bucket for a single model. Latency values only (the chart plots
// whichever metric the user toggles); `t` is unix seconds at the bucket edge.
export type LlmLabTimeseriesPoint = {
  t: number;
  responseTimeP50: number | null;
  responseTimeP95: number | null;
  ttftP50: number | null;
  ttftP95: number | null;
};

export type LlmLabModelSeries = {
  provider: string;
  model: string;
  points: LlmLabTimeseriesPoint[];
};

export type LlmLabTimeseries = {
  step: number; // bucket width in seconds, chosen server-side
  series: LlmLabModelSeries[];
};

// The metric keys the chart can plot — mirror the four latency table columns.
export type LlmLabChartMetric = "responseTimeP50" | "responseTimeP95" | "ttftP50" | "ttftP95";

export type LlmLabState = {
  aggregates: LlmLabAggregate[];
  loading: boolean;
  error: string | null;
  timeseries: LlmLabTimeseries | null;
  timeseriesLoading: boolean;
  timeseriesError: string | null;
};

export type GetAggregatesParams = {
  startDate: string;
  endDate: string;
  scope: LlmLabScope;
  agentId?: string;
  streaming?: "true" | "false";
  inputTokenBuckets?: LlmLabInputTokenBucket[];
  outputTokenBuckets?: LlmLabOutputTokenBucket[];
  region?: string;
  provider?: string;
};

export const getAggregates = createAsyncThunk("llmLab/getAggregates", async (params: GetAggregatesParams) => {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    scope: params.scope,
  });
  if (params.agentId) queryParams.append("agentId", params.agentId);
  if (params.streaming) queryParams.append("streaming", params.streaming);
  if (params.inputTokenBuckets && params.inputTokenBuckets.length > 0) {
    queryParams.append("inputTokenBucket", params.inputTokenBuckets.join(","));
  }
  if (params.outputTokenBuckets && params.outputTokenBuckets.length > 0) {
    queryParams.append("outputTokenBucket", params.outputTokenBuckets.join(","));
  }
  if (params.region) queryParams.append("region", params.region);
  if (params.provider) queryParams.append("provider", params.provider);

  const { data } = await makeRequest<{ data: LlmLabAggregate[] }>(`/v1/llm-lab/aggregates?${queryParams}`);
  return data;
});

// Same filter surface as getAggregates — the server derives the bucket step
// from the date range, so the params are identical.
export const getTimeseries = createAsyncThunk("llmLab/getTimeseries", async (params: GetAggregatesParams) => {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    scope: params.scope,
  });
  if (params.agentId) queryParams.append("agentId", params.agentId);
  if (params.streaming) queryParams.append("streaming", params.streaming);
  if (params.inputTokenBuckets && params.inputTokenBuckets.length > 0) {
    queryParams.append("inputTokenBucket", params.inputTokenBuckets.join(","));
  }
  if (params.outputTokenBuckets && params.outputTokenBuckets.length > 0) {
    queryParams.append("outputTokenBucket", params.outputTokenBuckets.join(","));
  }
  if (params.region) queryParams.append("region", params.region);
  if (params.provider) queryParams.append("provider", params.provider);

  const { data } = await makeRequest<{ data: LlmLabTimeseries }>(`/v1/llm-lab/timeseries?${queryParams}`);
  return data;
});

const initialState: LlmLabState = {
  aggregates: [],
  loading: false,
  error: null,
  timeseries: null,
  timeseriesLoading: false,
  timeseriesError: null,
};

const llmLabSlice = createSlice({
  name: "llmLab",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getAggregates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAggregates.fulfilled, (state, action) => {
      state.loading = false;
      state.aggregates = action.payload;
    });
    builder.addCase(getAggregates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch LLM Lab aggregates";
    });
    builder.addCase(getTimeseries.pending, (state) => {
      state.timeseriesLoading = true;
      state.timeseriesError = null;
    });
    builder.addCase(getTimeseries.fulfilled, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseries = action.payload;
    });
    builder.addCase(getTimeseries.rejected, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseriesError = action.error.message || "Failed to fetch LLM Lab timeseries";
    });
  },
});

export const { clear } = llmLabSlice.actions;
export const llmLabReducer = llmLabSlice.reducer;
